import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { useAppKit, useAppKitState } from '@reown/appkit/react'
import { ReactComponent as PlusIcon } from 'assets/svg/icons/Plus.svg'
import { ReactComponent as SendIcon } from 'assets/svg/icons/Send.svg'
import CopyHelper from 'components/AccountDetails/Copy'
import CategoryView from 'components/SearchModal/CategoryVIew'
import CurrencyList, { currencyKey } from 'components/SearchModal/CurrencyList'
import { WarningContent, WarningSection } from 'components/swap/SwapDetailsDropdown'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { XKDK_ADDRESSES } from 'constants/addresses'
import { categoriesForChain, CATEGORY_KEYS, CategoryKey } from 'constants/tokenLists/categories'
import { useFilteredSortedTokens, useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { SendTokenInputError, useSendToken } from 'hooks/useSendToken'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { Context, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ExternalLink as LinkIcon, Info as InfoIcon } from 'react-feather'
import { TbLogout as LogOutIcon } from 'react-icons/tb'
import AutoSizer from 'react-virtualized-auto-sizer'
import { useCurrencyBalance } from 'state/connection/hooks'
import { useAppDispatch } from 'state/hooks'
import { WrappedTokenInfo } from 'state/lists/wrappedTokenInfo'
import styled, { DefaultTheme, ThemeContext } from 'styled-components'

import useENSName from '../../hooks/useENSName'
import { clearAllTransactions } from '../../state/transactions/reducer'
import { ExternalLink, LinkStyledButton, ThemedText } from '../../theme'
import { shortenAddress } from '../../utils'
import { ExplorerDataType, getExplorerLink } from '../../utils/getExplorerLink'
import { ButtonLight, ButtonSecondary } from '../Button'
import { AutoRow } from '../Row'
import Transaction from './Transaction'

const UpperSection = styled.div`
  position: relative;

  h5 {
    margin: 0;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    font-weight: 400;
  }

  h5:last-child {
    margin-bottom: 0px;
  }

  h4 {
    margin-top: 0;
    font-weight: 500;
  }
`

const InfoCard = styled.div`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.cardBorderSecondary};
  background: ${({ theme }) => theme.cardSecondary};
  border-radius: 8px;
  position: relative;
  display: grid;
  grid-row-gap: 16px;
  margin-bottom: 4px;
`

const AccountGroupingRow = styled.div`
  ${({ theme }) => theme.flexRowNoWrap};
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  min-width: 0;
  width: 100%;
  font-weight: 400;
  color: ${({ theme }) => theme.deprecated_text1};
  align-items: flex-start;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    align-items: center !important;
  `}

  div {
    ${({ theme }) => theme.flexRowNoWrap}
    align-items: center;
  }
`
const YourAccount = styled.div`
  h5 {
    margin: 0 0 1rem 0;
    font-weight: 400;
  }

  h4 {
    margin: 0;
    font-weight: 500;
  }
`

const LowerSection = styled.div`
  ${({ theme }) => theme.flexColumnNoWrap}
  padding: 1.5rem;
  flex-grow: 1;
  overflow: auto;
  min-height: 240px;
  background: ${({ theme }) => theme.cardTertiary};
  border: 1px solid rgba(70, 96, 129, 0.5);
  gap: 12px;
  border-radius: 8px;
  height: 390px;

  h5 {
    margin: 0;
    font-weight: 400;
    color: ${({ theme }) => theme.deprecated_text3};
  }
`

const AccountControl = styled.div`
  display: flex;
  justify-content: space-between;
  min-width: 0;
  flex: 1;
  overflow: hidden;

  font-weight: 500;
  font-size: 1rem;

  a:hover {
    text-decoration: underline;
  }

  p {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
`

const AddressLink = styled(ExternalLink)`
  font-size: 0.825rem;
  color: ${({ theme }) => theme.buttonPrimary};
  display: flex;
  gap: 4px;
  :hover {
    color: ${({ theme }) => theme.textPrimary};
  }
`

const TransactionListWrapper = styled.div`
  ${({ theme }) => theme.flexColumnNoWrap};
  display: flex;
  flex-direction: column;
  gap: 7px;
`

const WalletAction = styled(ButtonSecondary)`
  width: fit-content;
  font-weight: 400;
  font-size: 0.825rem;
  padding: 4px 6px;
  flex-shrink: 0;
  :hover {
    cursor: pointer;
    text-decoration: underline;
  }
`

const ActionButton = styled(ButtonLight)`
  font-weight: 400;
  font-size: 0.825rem;
  padding: 4px 6px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  gap: 6px;
  > span {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  svg {
    flex-shrink: 0;
  }
  :hover {
    cursor: pointer;
  }
`

const TabRow = styled.div`
  display: flex;
  width: 100%;

  padding: 16px 20%;
  justify-content: space-evenly;
  font-weight: 500;
  color: ${(props) => (props.color === 'blue' ? ({ theme }) => theme.deprecated_primary1 : 'inherit')};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 1rem;
  `};

  border-bottom: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  margin-bottom: 12px;
`

const TabItem = styled(ThemedText.DeprecatedBody)`
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
  }
`

const PorfolioTabFlex = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  gap: 12px;
  border-radius: 8px;
  height: 360px;
  overflow: auto;
  padding: 12px 0;
`

const RowMinGap = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const ColumnMidGap = styled.div`
  display: flex;
  flex-direction: column;
  // gap: 12px;
`

export const ColumnMinGap = styled.div`
  display: flex;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 4px;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  flex: 1;
`
const AccountLinksRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
`

const ActionButtonsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const ActionButtonsRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`

const AddressText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  width: 100%;
  max-width: 220px;
  flex: 1;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    max-width: 80px;
  `};

  ${({ theme }) => theme.mediaWidth.upToMedium`
    max-width: 150px;
  `};
`

const ShowMobile = styled.div`
  display: none;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    display: block !important;
  `};
`

const ShowDesktop = styled.div`
  display: block;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    display: none !important;
  `};
`

function renderTransactions(transactions: string[]) {
  return (
    <TransactionListWrapper>
      {transactions.map((hash, i) => {
        return <Transaction key={i} hash={hash} />
      })}
    </TransactionListWrapper>
  )
}

interface PortfolioContentProps {
  tokens: WrappedTokenInfo[]
  balances: CurrencyAmount<Token>[]
  send: {
    mode: boolean
    selectedForSendKey: string | null
    onSelect: (currency: Currency, balance?: string) => void
    sendRecipient: string
    sendAmount: string
    onRecipientChange: (value: string) => void
    onAmountChange: (value: string) => void
    showSelectHint: boolean
    selectedToken: Currency | null
    isPending: boolean
    onClearSelection: VoidFunction
    onExecute?: VoidFunction
    inputError?: SendTokenInputError
  }
}

const PortfolioContent: React.FC<PortfolioContentProps> = ({ balances, tokens, send }: PortfolioContentProps) => {
  const { chainId } = useConnection()
  const xkdkAddress = XKDK_ADDRESSES[chainId]
  const xkdk = useToken(xkdkAddress)
  const categories = useMemo(() => categoriesForChain(chainId), [chainId])
  const [pickedCategories, setPickedCategories] = useState<CategoryKey[]>([CATEGORY_KEYS.MAIN])
  // Picks made on another chain (e.g. Main on Robinhood) don't apply here; with none left the list is unfiltered.
  const selectedCategories = useMemo(
    () => pickedCategories.filter((key) => categories.some(({ name }) => name === key)),
    [pickedCategories, categories]
  )
  const { showSelectHint, selectedToken: selectedTokenForSend, onClearSelection: onClearSendSelection } = send

  const tokenAddresses = useMemo(() => tokens.map((token) => token.wrapped.address), [tokens])
  const { prices: tokenPrices } = useGetTokenPrices(tokenAddresses)

  const pricesLoading = useMemo(() => {
    if (tokenPrices === null) return true
    if (!Array.isArray(tokenPrices)) return true
    if (tokenPrices.length !== tokenAddresses.length) return true
    return false
  }, [tokenPrices, tokenAddresses.length])

  const tokenPriceMap = useMemo(() => {
    if (!tokenPrices || !Array.isArray(tokenPrices)) return {}

    const priceMap: { [address: string]: number | null } = {}
    tokenAddresses.forEach((address, index) => {
      priceMap[address] = tokenPrices[index] !== undefined ? tokenPrices[index] : null
    })
    return priceMap
  }, [tokenAddresses, tokenPrices])

  const tokensWithoutXKDK = useMemo(
    () => tokens.filter((token) => token.wrapped.address !== xkdk?.address),
    [tokens, xkdk?.address]
  )

  const balancesByAddress = useMemo(() => {
    return (balances ?? []).reduce((acc, item) => {
      acc[item?.currency?.address] = item
      return acc
    }, {} as { [tokenAddress: string]: CurrencyAmount<Token> | undefined })
  }, [balances])

  const { filteredSortedTokensWithNative } = useFilteredSortedTokens({
    tokens: tokensWithoutXKDK,
    selectedCategories,
    balances: {
      data: balancesByAddress,
      isLoading: false,
    },
    tokenPrices: {
      data: tokenPrices,
      isLoading: pricesLoading,
    },
    allowNativeToken: true,
    nonZeroBalances: true,
  })

  useEffect(() => {
    if (!selectedTokenForSend) return
    const key = currencyKey(selectedTokenForSend)
    const isInList = filteredSortedTokensWithNative.some((c) => currencyKey(c) === key)
    if (!isInList) {
      onClearSendSelection()
    }
  }, [filteredSortedTokensWithNative, selectedTokenForSend, onClearSendSelection])

  const onSelectCategory = (name: CategoryKey) => {
    setPickedCategories((prev) => {
      const isSelected = prev.includes(name)
      if (isSelected) {
        const newSelection = prev.filter((key) => key !== name)
        return newSelection
      } else {
        return [...prev, name]
      }
    })
  }

  return (
    <ColumnMidGap>
      <CategoryView categories={categories} selected={selectedCategories} onSelect={onSelectCategory} />
      {showSelectHint && !selectedTokenForSend && (
        <WarningSection>
          <WarningContent>
            <InfoIcon size={16} style={{ flexShrink: 0 }} />
            Select a token to send
          </WarningContent>
        </WarningSection>
      )}
      <PorfolioTabFlex>
        <AutoSizer disableWidth>
          {({ height }) => (
            <CurrencyList
              height={height}
              currencies={filteredSortedTokensWithNative}
              isLoading={!filteredSortedTokensWithNative.length}
              isAddressSearch={false}
              tokenPriceMap={tokenPriceMap}
              showCurrencyAmount
              needMarginTop={false}
              send={send}
            />
          )}
        </AutoSizer>
      </PorfolioTabFlex>
    </ColumnMidGap>
  )
}

interface ActivityContentProps {
  pendingTransactions: string[]
  confirmedTransactions: string[]
  clearAllTransactionsCallback: () => void
}

const ActivityContent: React.FC<ActivityContentProps> = ({
  pendingTransactions,
  confirmedTransactions,
  clearAllTransactionsCallback,
}: ActivityContentProps) => {
  const theme = useContext(ThemeContext as Context<DefaultTheme>)

  return (
    <>
      {pendingTransactions.length > 0 || confirmedTransactions.length > 0 ? (
        <LowerSection>
          <AutoRow mb="1rem" style={{ justifyContent: 'space-between' }}>
            <ThemedText.DeprecatedBody>
              <>Recent Transactions</>
            </ThemedText.DeprecatedBody>
            <LinkStyledButton onClick={clearAllTransactionsCallback}>
              <>(clear all)</>
            </LinkStyledButton>
          </AutoRow>
          {renderTransactions(pendingTransactions)}
          {renderTransactions(confirmedTransactions)}
        </LowerSection>
      ) : (
        <LowerSection>
          <ThemedText.DeprecatedBody color={theme.deprecated_text1}>
            <>Your transactions will appear here...</>
          </ThemedText.DeprecatedBody>
        </LowerSection>
      )}
    </>
  )
}

interface AccountDetailsProps {
  pendingTransactions: string[]
  confirmedTransactions: string[]
  tokens?: WrappedTokenInfo[]
  balances?: any[]
}

export default function AccountDetails({
  pendingTransactions,
  confirmedTransactions,
  tokens,
  balances,
}: AccountDetailsProps) {
  const { chainId, account, disconnect } = useConnection()
  const { ENSName } = useENSName(account) ?? undefined
  const { open } = useAppKit()
  const { onramp } = useAppKitState()

  const handleBuyCrypto = () => open({ view: 'OnRampProviders' })
  const onRampEnabled = Array.isArray(onramp) && onramp.length > 0

  const [portfolioTab, setPortfolioTab] = useState(0)
  const theme = useContext(ThemeContext as Context<DefaultTheme>)
  const dispatch = useAppDispatch()

  // Send mode
  const [selectedTokenForSend, setSelectedTokenForSend] = useState<Currency | null>(null)
  const [sendRecipient, setSendRecipient] = useState('')
  const [sendAmount, setSendAmount] = useState('')
  const [showSelectHint, setShowSelectHint] = useState(false)
  const selectedTokenBalance = useCurrencyBalance(account, selectedTokenForSend ?? undefined, REFETCH_POLICY_FAST)
  const selectedTokenBalanceString = useMemo(() => selectedTokenBalance?.toExact(), [selectedTokenBalance])

  const selectedForSendKey = useMemo(
    () => (selectedTokenForSend ? currencyKey(selectedTokenForSend) : null),
    [selectedTokenForSend]
  )

  const {
    execute: executeSend,
    isPending: isSendPending,
    inputError: sendInputError,
  } = useSendToken(selectedTokenForSend, sendRecipient, sendAmount, selectedTokenBalanceString)

  const handleSelectForSend = useCallback(
    (currency: Currency) => {
      const key = currencyKey(currency)
      if (selectedForSendKey === key) {
        setSelectedTokenForSend(null)
        setSendRecipient('')
        setSendAmount('')
      } else {
        setSelectedTokenForSend(currency)
        setSendRecipient('')
        setSendAmount('')
      }
      setShowSelectHint(false)
    },
    [selectedForSendKey]
  )

  const clearSendSelection = useCallback(() => {
    setSelectedTokenForSend(null)
    setSendRecipient('')
    setSendAmount('')
    setShowSelectHint(false)
  }, [])

  const handleSendClick = useCallback(async () => {
    if (!selectedTokenForSend) {
      setShowSelectHint(true)
      return
    }
    if (executeSend) {
      try {
        await executeSend()
        setSelectedTokenForSend(null)
        setSendRecipient('')
        setSendAmount('')
      } catch (error) {
        console.error('Send failed:', error)
      }
    }
  }, [selectedTokenForSend, executeSend])

  const handlePortfolioTabClick = (tab: 0 | 1) => {
    setPortfolioTab(tab)
  }

  const clearAllTransactionsCallback = useCallback(() => {
    if (chainId) dispatch(clearAllTransactions({ chainId }))
  }, [dispatch, chainId])

  const renderTabContent = () => {
    switch (portfolioTab) {
      case 0:
        return (
          <PortfolioContent
            tokens={tokens ?? []}
            balances={balances ?? []}
            send={{
              mode: true,
              selectedForSendKey,
              onSelect: handleSelectForSend,
              sendRecipient,
              sendAmount,
              onRecipientChange: setSendRecipient,
              onAmountChange: setSendAmount,
              showSelectHint,
              selectedToken: selectedTokenForSend,
              isPending: isSendPending,
              onClearSelection: clearSendSelection,
              onExecute: handleSendClick,
              inputError: sendInputError,
            }}
          />
        )
      case 1:
        return (
          <ActivityContent
            pendingTransactions={pendingTransactions}
            confirmedTransactions={confirmedTransactions}
            clearAllTransactionsCallback={clearAllTransactionsCallback}
          />
        )
      default:
        return null
    }
  }

  const renderAccountLinks = () => (
    <AccountLinksRow>
      {account && (
        <CopyHelper toCopy={account} iconPosition="left" noPadding gap="2px">
          Copy Address
        </CopyHelper>
      )}
      {chainId && account && (
        <AddressLink href={getExplorerLink(chainId, ENSName ?? account, ExplorerDataType.ADDRESS)}>
          <LinkIcon size={16} />
          <> View on Explorer </>
        </AddressLink>
      )}
    </AccountLinksRow>
  )

  return (
    <>
      <UpperSection>
        <YourAccount>
          <InfoCard>
            <ColumnMinGap>
              <AccountGroupingRow data-testid="web3-account-identifier-row">
                <AccountControl>
                  <ColumnMinGap>
                    <AddressText>{ENSName ? ENSName : account && shortenAddress(account)}</AddressText>
                    <ShowDesktop>{renderAccountLinks()}</ShowDesktop>
                  </ColumnMinGap>
                </AccountControl>
                <WalletAction
                  onClick={() => {
                    disconnect()
                  }}
                >
                  <RowMinGap>
                    <LogOutIcon size={16} />
                    Disconnect
                  </RowMinGap>
                </WalletAction>
              </AccountGroupingRow>

              <ShowMobile>{renderAccountLinks()}</ShowMobile>
            </ColumnMinGap>

            <AccountGroupingRow>
              <AccountControl>
                <ActionButtonsColumn>
                  <ActionButtonsRow>
                    {onRampEnabled && (
                      <ActionButton
                        onClick={() => {
                          void handleBuyCrypto()
                        }}
                      >
                        <PlusIcon width={16} height={16} />
                        Buy Crypto
                      </ActionButton>
                    )}

                    <ActionButton onClick={handleSendClick} disabled={isSendPending}>
                      <SendIcon width={16} height={16} />
                      <span>
                        {isSendPending
                          ? 'Sending...'
                          : selectedTokenForSend
                          ? `Send ${selectedTokenForSend.symbol}`
                          : 'Send'}
                      </span>
                    </ActionButton>
                  </ActionButtonsRow>
                </ActionButtonsColumn>
              </AccountControl>
            </AccountGroupingRow>
          </InfoCard>
        </YourAccount>
      </UpperSection>

      <TabRow>
        <TabItem
          fontWeight={500}
          fontSize={18}
          color={portfolioTab === 0 ? theme.textPrimary : theme.textSecondary}
          onClick={() => handlePortfolioTabClick(0)}
        >
          Portfolio
        </TabItem>
        <TabItem
          fontWeight={500}
          fontSize={18}
          color={portfolioTab === 1 ? theme.textPrimary : theme.textSecondary}
          onClick={() => handlePortfolioTabClick(1)}
        >
          Activity
        </TabItem>
      </TabRow>
      {renderTabContent()}
    </>
  )
}
